import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import Box from "@mui/material/Box";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
} from "chart.js";
import moment from "moment";
import PropTypes from "prop-types";
import { useLayoutEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { createPortal } from "react-dom";

import { VELOCITY_WINDOW_PANEL_MARGIN_DAYS } from "../../constants";

ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  ChartTooltip,
  LineController,
  LineElement,
  LinearScale,
  PointElement
);

// Field of each ASIN's /velocity-tool-sales entry holding the units sold per
// day, aligned with its `dates`.
const UNITS_SOLD_FIELD = "actual_sold_units";

// Summarises the velocity tool window of a product (one ASIN, or a parent's
// children added up per date). Returns null when there is no window.
const buildVelocityWindowSummary = (
  predictedUnitsByAsin,
  today = moment().format("YYYY-MM-DD")
) => {
  const byDate = new Map();
  let hasSoldData = false;

  Object.values(predictedUnitsByAsin || {}).forEach((entry) => {
    if (!entry || !Array.isArray(entry.dates)) return;
    const sold = entry[UNITS_SOLD_FIELD];
    if (Array.isArray(sold)) hasSoldData = true;

    entry.dates.forEach((date, i) => {
      const day = byDate.get(date) || { original: 0, adjusted: 0, sold: 0 };
      day.original += entry.original_target?.[i] ?? 0;
      day.adjusted += entry.adj_target?.[i] ?? 0;
      day.sold += sold?.[i] ?? 0;
      byDate.set(date, day);
    });
  });

  if (byDate.size === 0) return null;

  const dates = Array.from(byDate.keys()).sort();
  const start = dates[0];
  const end = dates[dates.length - 1];

  const days = dates.map((date) => ({ date, ...byDate.get(date) }));
  const sum = (list, field) => list.reduce((acc, d) => acc + d[field], 0);

  // Only finished days (before today) are compared, so today's partial sales
  // don't read as falling behind.
  const completedDays = days.filter((d) => d.date < today);
  const soldToDate = sum(completedDays, "sold");
  const targetToDate = sum(completedDays, "original");
  const difference = soldToDate - targetToDate;

  return {
    start,
    end,
    days,
    hasSoldData,
    currentDay: days.filter((d) => d.date <= today).length,
    // 0 once the window has started.
    daysUntilStart: Math.max(0, moment(start).diff(moment(today), "days")),
    totalDays: days.length,
    sold: sum(
      days.filter((d) => d.date <= today),
      "sold"
    ),
    windowTarget: sum(days, "original"),
    comparison:
      hasSoldData && completedDays.length > 0
        ? {
            difference,
            percent:
              targetToDate > 0
                ? (Math.abs(difference) / targetToDate) * 100
                : null,
          }
        : null,
  };
};

// Whether today falls from VELOCITY_WINDOW_PANEL_MARGIN_DAYS before the window
// starts to VELOCITY_WINDOW_PANEL_MARGIN_DAYS after it ends.
const isInPanelPeriod = (summary, today = moment().format("YYYY-MM-DD")) => {
  const showFrom = moment(summary.start)
    .subtract(VELOCITY_WINDOW_PANEL_MARGIN_DAYS, "days")
    .format("YYYY-MM-DD");
  const showUntil = moment(summary.end)
    .add(VELOCITY_WINDOW_PANEL_MARGIN_DAYS, "days")
    .format("YYYY-MM-DD");
  return today >= showFrom && today <= showUntil;
};

const formatDay = (date) => moment(date).format("MMM D");

const CHART_MAX_WIDTH = 220;
const CHART_HEIGHT = 44;
// Space kept between the summary and the right edge of the screen.
const SCREEN_EDGE_MARGIN = 16;

// Width from the element's left edge to the right edge of the screen. The
// products table is wider than the screen, so the summary matches the table
// only up to what's actually visible.
const useWidthToScreenEdge = (ref) => {
  const [width, setWidth] = useState(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const measure = () => {
      const { left } = node.getBoundingClientRect();
      setWidth(
        document.documentElement.clientWidth - left - SCREEN_EDGE_MARGIN
      );
    };
    measure();

    // The row's left-hand cells can change width after load, moving us.
    const row = node.closest("tr") || node.parentElement;
    const observer = new ResizeObserver(measure);
    if (row) observer.observe(row);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [ref]);

  return width;
};

// Dashed vertical line on today's bar. Reads the index from
// options.plugins.todayMarker.index (-1 or missing draws nothing).
const todayMarkerPlugin = {
  id: "todayMarker",
  afterDatasetsDraw(chart, _args, options) {
    const index = options?.index;
    if (index === undefined || index < 0) return;
    const x = chart.scales.x.getPixelForValue(index);
    const { top, bottom } = chart.chartArea;
    const { ctx } = chart;
    ctx.save();
    ctx.strokeStyle = "#475569";
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.restore();
  },
};

const DifferenceChip = ({ difference, percent }) => {
  const isAbove = difference >= 0;
  const Arrow = isAbove ? NorthEastIcon : SouthEastIcon;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 10px",
        borderRadius: "16px",
        whiteSpace: "nowrap",
        fontWeight: 600,
        color: isAbove ? "#15803d" : "#b91c1c",
        backgroundColor: isAbove ? "#dcfce7" : "#fee2e2",
      }}
    >
      <Arrow sx={{ fontSize: "16px" }} />
      {isAbove ? "Above" : "Behind"} {Math.abs(difference).toLocaleString()}
      {percent !== null && ` ~ ${percent.toFixed(1)}%`}
    </Box>
  );
};

DifferenceChip.propTypes = {
  difference: PropTypes.number.isRequired,
  percent: PropTypes.number,
};

// The plot is too short for Chart.js's own tooltip, which is drawn inside the
// canvas and gets cut. This one is rendered on document.body instead, above
// the hovered day (or below it when there's no room above).
const PlotTooltip = ({ day, hasSoldData, hover }) => {
  const placeBelow = hover.top < 90;
  const row = (color, label, value) => (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "2px",
          backgroundColor: color,
        }}
      />
      {label}: {value.toLocaleString()}
    </div>
  );

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        left: hover.left,
        top: placeBelow ? hover.bottom + 6 : hover.top - 6,
        transform: placeBelow ? "translateX(-50%)" : "translate(-50%, -100%)",
        zIndex: 1500,
        pointerEvents: "none",
        padding: "6px 8px",
        borderRadius: "4px",
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        color: "white",
        fontFamily: "Inter",
        fontSize: 11,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
      }}
    >
      <div style={{ fontWeight: 600 }}>{formatDay(day.date)}</div>
      {row("#36A2EB", "Original target", day.original)}
      {row("#FF6384", "Adjusted target", day.adjusted)}
      {hasSoldData && row("rgba(148, 163, 184, 0.9)", "Units sold", day.sold)}
    </Box>,
    document.body
  );
};

PlotTooltip.propTypes = {
  day: PropTypes.object.isRequired,
  hasSoldData: PropTypes.bool,
  hover: PropTypes.object.isRequired,
};

// Rendered only once there is a summary, so the width measurement runs
// against a mounted box.
const SummaryCard = ({
  summary,
  upcomingDays = 0,
  upcomingTargetUnits = 0,
  upcomingShown = false,
  onToggleUpcoming,
}) => {
  const containerRef = useRef(null);
  const widthToScreenEdge = useWidthToScreenEdge(containerRef);
  // { index, left, top, bottom } of the hovered day, in viewport pixels.
  const [hover, setHover] = useState(null);

  const chartData = {
    labels: summary.days.map((d) => formatDay(d.date)),
    datasets: [
      {
        type: "line",
        label: "Original target",
        data: summary.days.map((d) => d.original),
        borderColor: "#36A2EB",
        backgroundColor: "#36A2EB",
        borderWidth: 1.5,
        pointRadius: 0,
        order: 1,
      },
      {
        type: "line",
        label: "Adjusted target",
        data: summary.days.map((d) => d.adjusted),
        borderColor: "#FF6384",
        backgroundColor: "#FF6384",
        borderWidth: 1.5,
        pointRadius: 0,
        order: 0,
      },
      ...(summary.hasSoldData
        ? [
            {
              type: "bar",
              label: "Units sold",
              data: summary.days.map((d) => d.sold),
              backgroundColor: "rgba(148, 163, 184, 0.55)",
              order: 2,
            },
          ]
        : []),
    ],
  };

  const today = moment().format("YYYY-MM-DD");
  const todayIndex = summary.days.findIndex((d) => d.date === today);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      todayMarker: { index: todayIndex },
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          const index = tooltip.dataPoints?.[0]?.dataIndex;
          if (tooltip.opacity === 0 || index === undefined) {
            setHover(null);
            return;
          }
          const rect = chart.canvas.getBoundingClientRect();
          // Same day: keep the state as is so the chart doesn't re-render.
          setHover((prev) =>
            prev?.index === index
              ? prev
              : {
                  index,
                  left: rect.left + tooltip.caretX,
                  top: rect.top,
                  bottom: rect.bottom,
                }
          );
        },
      },
    },
    scales: {
      x: { display: false },
      y: { display: false, beginAtZero: true },
    },
  };

  const statLabel = { color: "#94a3b8", paddingRight: "8px" };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        width:
          widthToScreenEdge !== null
            ? `min(55vw, ${Math.max(widthToScreenEdge, 0)}px)`
            : "55vw",
        boxSizing: "border-box",
        overflow: "hidden",
        marginBottom: "6px",
        padding: "6px 12px",
        backgroundColor: "white",
        border: "1px solid #e2e8f0",
        borderRadius: "4px",
        fontFamily: "Inter",
        fontSize: 12,
        color: "#475569",
      }}
    >
      <Box sx={{ fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
        {formatDay(summary.start)} - {formatDay(summary.end)}
      </Box>
      <Box
        component="table"
        sx={{
          flexShrink: 0,
          borderCollapse: "collapse",
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <tbody>
          <tr>
            {summary.daysUntilStart > 0 ? (
              <td colSpan={2}>
                Starts in {summary.daysUntilStart}{" "}
                {summary.daysUntilStart === 1 ? "day" : "days"}
              </td>
            ) : (
              <>
                <td style={statLabel}>Day</td>
                <td>
                  {summary.currentDay} / {summary.totalDays}
                </td>
              </>
            )}
          </tr>
          <tr>
            <td style={statLabel}>Sold</td>
            <td>
              {summary.hasSoldData ? summary.sold.toLocaleString() : "-"} /{" "}
              {summary.windowTarget.toLocaleString()}
            </td>
          </tr>
        </tbody>
      </Box>
      {/* The plot is the only part that gives up space when the summary is
          narrow. Chart.js sizes the canvas from this box, so it must be
          bounded (no grow, minWidth 0) and relatively positioned. */}
      <Box
        sx={{
          position: "relative",
          flex: `0 1 ${CHART_MAX_WIDTH}px`,
          minWidth: 0,
          height: `${CHART_HEIGHT}px`,
          overflow: "hidden",
        }}
      >
        <Bar
          data={chartData}
          options={chartOptions}
          plugins={[todayMarkerPlugin]}
        />
      </Box>
      {hover && summary.days[hover.index] && (
        <PlotTooltip
          day={summary.days[hover.index]}
          hasSoldData={summary.hasSoldData}
          hover={hover}
        />
      )}
      {/* Right after the plot, not pinned to the far edge: the products table
          can be wider than what's visible, which would hide the chip. */}
      {summary.comparison && (
        <Box sx={{ flexShrink: 0 }}>
          <DifferenceChip {...summary.comparison} />
        </Box>
      )}
      {upcomingDays > 0 && onToggleUpcoming && (
        <Box
          component="button"
          type="button"
          onClick={onToggleUpcoming}
          aria-expanded={upcomingShown}
          sx={{
            flexShrink: 0,
            padding: 0,
            border: "none",
            background: "none",
            color: "#1d4ed8",
            fontFamily: "Inter",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {upcomingShown
            ? `‹ Hide next ${upcomingDays} days`
            : `Next ${upcomingDays} days${
                upcomingTargetUnits > 0
                  ? `: ${upcomingTargetUnits.toLocaleString()} units`
                  : ""
              } ›`}
        </Box>
      )}
    </Box>
  );
};

SummaryCard.propTypes = {
  summary: PropTypes.object.isRequired,
  upcomingDays: PropTypes.number,
  upcomingTargetUnits: PropTypes.number,
  upcomingShown: PropTypes.bool,
  onToggleUpcoming: PropTypes.func,
};

// upcomingDays / upcomingTargetUnits / upcomingShown / onToggleUpcoming: the
// "Next N days" link after the Above/Behind chip, which shows and hides the
// table's upcoming-day columns.
const VelocityWindowSummary = ({
  predictedUnitsData,
  upcomingDays,
  upcomingTargetUnits,
  upcomingShown,
  onToggleUpcoming,
}) => {
  const summary = buildVelocityWindowSummary(predictedUnitsData);
  return summary && isInPanelPeriod(summary) ? (
    <SummaryCard
      summary={summary}
      upcomingDays={upcomingDays}
      upcomingTargetUnits={upcomingTargetUnits}
      upcomingShown={upcomingShown}
      onToggleUpcoming={onToggleUpcoming}
    />
  ) : null;
};

VelocityWindowSummary.propTypes = {
  predictedUnitsData: PropTypes.object,
  upcomingDays: PropTypes.number,
  upcomingTargetUnits: PropTypes.number,
  upcomingShown: PropTypes.bool,
  onToggleUpcoming: PropTypes.func,
};

export { buildVelocityWindowSummary, isInPanelPeriod };
export default VelocityWindowSummary;
